import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, UserCheck2, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { calendarAPI, userAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

/**
 * Inline search chip: type to search active users, click a suggestion
 * to register that person directly (skips the invite step) for the
 * given event. Fires the same `registration_confirmed` email + in-app
 * notification the backend sends on self-registration.
 *
 * Kept intentionally lean — no modal, no multi-select, one action per
 * click. For batch registration the panel still exposes the full
 * BulkInviteDialog via "Register Agent".
 */
export default function InlineRegisterAgent({ eventId, onDone }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingUid, setPendingUid] = useState(null);
  const boxRef = useRef(null);

  // Debounced remote search
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setResults([]);
      return () => {};
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await userAPI.listPaginated({ search: term, page: 1, page_size: 8, status: 'active' });
        if (!cancelled) setResults(res.data.items || []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const register = async (u) => {
    if (pendingUid) return;
    setPendingUid(u.user_id);
    try {
      const res = await calendarAPI.inviteParticipants(
        eventId,
        [u.user_id],
        null,
        { register_directly: true },
      );
      const created = (res.data?.invited || []).length;
      const skipped = (res.data?.skipped_existing || []).length;
      if (created === 0 && skipped > 0) {
        toast.info(`${u.name || u.email} is already registered for this event.`);
      } else {
        toast.success(`Registered ${u.name || u.email}. Email + in-app notification sent.`);
      }
      setQ('');
      setResults([]);
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not register user'));
    } finally {
      setPendingUid(null);
    }
  };

  return (
    <div className="relative flex-1 max-w-xs" ref={boxRef} data-testid="inline-register-agent">
      <div className="flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 h-8 focus-within:ring-1 focus-within:ring-emerald-500 focus-within:border-emerald-500">
        <Search size={13} className="text-slate-400 shrink-0" />
        <input
          type="text"
          value={q}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          placeholder="Register agent by name/email…"
          className="w-full text-sm bg-transparent focus:outline-none"
          data-testid="inline-register-agent-input"
        />
        {q && (
          <button
            type="button"
            onClick={() => { setQ(''); setResults([]); }}
            className="text-slate-400 hover:text-slate-600"
            aria-label="Clear"
            data-testid="inline-register-agent-clear"
          >
            <X size={13} />
          </button>
        )}
      </div>

      {open && q.trim().length >= 2 && (
        <div
          className="absolute z-30 mt-1 w-80 max-h-72 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg"
          data-testid="inline-register-agent-dropdown"
        >
          {loading ? (
            <div className="p-3 flex items-center gap-2 text-xs text-slate-500">
              <Loader2 size={13} className="animate-spin" /> Searching…
            </div>
          ) : results.length === 0 ? (
            <div className="p-3 text-xs text-slate-500">No matching users.</div>
          ) : (
            <ul className="py-1">
              {results.map((u) => {
                const isPending = pendingUid === u.user_id;
                return (
                  <li key={u.user_id}>
                    <button
                      type="button"
                      onClick={() => register(u)}
                      disabled={!!pendingUid}
                      className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-left hover:bg-emerald-50 disabled:opacity-60"
                      data-testid={`inline-register-agent-option-${u.user_id}`}
                    >
                      <div className="min-w-0">
                        <div className="text-sm text-slate-800 truncate">{u.name || u.email}</div>
                        <div className="text-[11px] text-slate-500 truncate">{u.email}</div>
                      </div>
                      <span className="shrink-0">
                        {isPending ? (
                          <Loader2 size={13} className="animate-spin text-emerald-600" />
                        ) : (
                          <UserCheck2 size={13} className="text-emerald-600" />
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="border-t border-slate-100 px-3 py-1.5 text-[11px] text-slate-500">
            One click = registered + email sent
          </div>
        </div>
      )}
    </div>
  );
}
